<template>
  <div class="page page-family">
    <div class="page__wrap" data-animate="page-wrap" v-if="family">
      <!-- Header -->
      <PagesProductFamilyHeader :family="family" class="p-top-3" />
      <div data-animate="page-content">
        <!-- Products -->
        <LazyPagesProductFamilyProducts
          class="m-top-3 p-bot-3"
          :family="family"
        />
        <!-- Editorial -->
        <LazyEditorialList
          class="p-bot-3"
          v-if="family?.editorialContent?.length"
          :content="family.editorialContent"
        />
        <!-- Finishings -->
        <LazyPagesFinishingsListProduct
          v-if="finishings?.length || internalFinishings.length"
          class="p-bot-3"
          :finishings="finishings"
          :internalFinishings="internalFinishings"
          :wireFinishings="wireFinishings"
          :baseFinishings="baseFinishings"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useCraftStore } from '@/store/craft.js'

import queries from '@/graphql/craft/queries/index.js'

import {
  onBeforeEnter,
  onEnter,
  onLeave,
} from '~/assets/js/page-transitions/family.js'

definePageMeta({
  layout: 'default',
  pageTransition: {
    onBeforeEnter: (el) => {
      onBeforeEnter(el)
    },
    onEnter: async (el, done) => {
      const tl = onEnter(el, done)
      await tl.delay(0.5).play()
      done()
    },
    onLeave: async (el, done) => {
      const tl = onLeave(el)
      await tl.play()
      window.lenis.scrollTo(0, { immediate: true })
      done()
    },
  },
})

const { t } = useI18n()

// Fetch Craft
const { configuration } = useCraftStore()

const query = queries.products.getFamily
const variables = computed(() => ({
  site: configuration.site,
  marketId: configuration.marketId,
  familySlug: useRoute().params.slug,
}))
const { data } = await useGraphql(query, variables)

if (!data.value.productsEntries?.[0]) {
  throw createError({ statusCode: 404 })
}

const family = computed(() => data.value.productsEntries?.[0])
const finishings = computed(() => {
  const finishingsSet = new Set(
    family.value.children
      .flatMap((child) => child.relatedFinishing || [])
      .map((finishing) => JSON.stringify(finishing))
  )
  return [...finishingsSet].map((item) => JSON.parse(item))
})
const internalFinishings = computed(() => {
  const finishingsSet = new Set(
    family.value.children
      .flatMap((child) => child.relatedInternalFinishing || [])
      .map((finishing) => JSON.stringify(finishing))
  )
  return [...finishingsSet].map((item) => JSON.parse(item))
})

const wireFinishings = computed(() => {
  const finishingsSet = new Set(
    family.value.children
      .flatMap((child) => child.relatedWireFinishing || [])
      .map((finishing) => JSON.stringify(finishing))
  )
  return [...finishingsSet].map((item) => JSON.parse(item))
})

const baseFinishings = computed(() => {
  const finishingsSet = new Set(
    family.value.children
      .flatMap((child) => child.relatedBaseFinishing || [])
      .map((finishing) => JSON.stringify(finishing))
  )
  return [...finishingsSet].map((item) => JSON.parse(item))
})

const seo = computed(() => data.value.productsEntries?.[0]?.seo || {})
const localeParams = computed(
  () => data.value?.productsEntries?.[0]?.localized || {}
)

// Seo
useHead({
  titleTemplate: '%s - %collection',
  templateParams: {
    collection: t('bucket.seoTitleCollection'),
  },
})

useSeo({
  ...seo.value,
  localeParams: useHelpers().generateSeoLocaleParams(localeParams, 'slug'),
  meta: [
    {
      name: 'description',
      content:
        seo.value.meta?.description ||
        t('bucket.seoFamilyDescriptionPlaceholder', {
          collection: family.value?.title,
        }),
    },
  ],
  market: family?.value?.market,
})
</script>
